'use client'

import { formatCoin, formatPrice } from '@polaris/core/utils'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useSelectedTickerInfo } from '@/src/hooks/token-info/use-selected-ticker'
import { assetDataQuery } from '@/src/queries/keys/token-info/asset-data'
import { Skeleton } from '../ui/skeleton'

const titles = [
  'Volume (24h)',
  'Market cap',
  'Fully-diluted value',
  'Total supply',
]

function StatsSkeleton() {
  return (
    <div className='grid grid-cols-2 gap-4 py-6 @2xl/token-info-main:flex @2xl/token-info-main:w-full'>
      {titles.map((title, index) => (
        <div key={index} className='flex flex-col md:gap-1 w-full'>
          <span className='text-xs md:text-sm text-secondary'>{title}</span>
          <div className='py-2'>
            <Skeleton className='w-24 h-6 rounded-[8px]' />
          </div>
        </div>
      ))}
    </div>
  )
}

export default function Stats() {
  const { chain, denom } = useSelectedTickerInfo()

  const { data: geckoData, isLoading } = useQuery({
    ...assetDataQuery({ chainId: chain, denom }),
    enabled: !!chain && !!denom,
  })

  const aggregatedData = useMemo(() => {
    if (!geckoData) return

    return [
      {
        title: titles[0],
        value: geckoData.market_data.total_volume.usd,
      },
      {
        title: titles[1],
        value: geckoData.market_data.market_cap.usd,
      },
      {
        title: titles[2],
        value: geckoData.market_data.fully_diluted_valuation.usd,
      },
      {
        title: titles[3],
        value: geckoData.market_data.total_supply,
        nonUsd: true,
      },
    ]
  }, [geckoData])

  if (isLoading) {
    return <StatsSkeleton />
  }

  return (
    <div className='grid grid-cols-2 gap-4 gap-y-6 py-6 @2xl/token-info-main:flex @2xl/token-info-main:w-full'>
      {aggregatedData?.map(({ title, value, nonUsd }, index) => (
        <div key={index} className='flex flex-col md:gap-1 w-full'>
          <span className='text-sm text-secondary'>{title}</span>
          <span className='text-3xl lg:text-4xl font-serif font-extralight text-primary'>
            {nonUsd
              ? formatCoin(
                  { amount: value },
                  {
                    notation: 'compact',
                    maximumFractionDigits: 2,
                  },
                )
              : formatPrice(value, {
                  notation: 'compact',
                  maximumFractionDigits: 2,
                  roundingPriority: 'auto',
                })}
          </span>
        </div>
      ))}
    </div>
  )
}
